/**
 * App updates from the zw-cloud update proxy (#351, #382).
 *
 * Rust (`get_update_channel`) says how this build was installed and gives the
 * manifest URL for it, built from the baked cloud URL:
 * - inPlace: the AppImage. Check here, then `install_app_update` downloads,
 *   checks the signature, and replaces the AppImage.
 * - link: the .deb and the Android APK. Check here, then open the download
 *   link in the browser.
 * - mac: a packaged macOS app. Check the darwin manifest here. A newer
 *   version (`install: "manual"`, no url) shows the source rebuild steps.
 *   The rebuild runs only when a checkout is already known. No download
 *   and no in-place install.
 * - source: a source or dev build. The published beta tag check.
 * - none: no update check on this build.
 *
 * The manifest answers 204 when up to date, 200 when a newer build exists,
 * and 429 when this IP checked too often. A download manifest carries `url`.
 * The macOS manifest carries `install: "manual"` and no url.
 */

import { openUrl } from "@tauri-apps/plugin-opener";
import { z } from "zod";
import { decodeWithSchema } from "./cloud/zodResult";
import { tauriOnlyInvoke } from "./invoke";
import { just, nothing, type Maybe } from "./maybe";
import { err, ok, type Result } from "./result";

export type UpdateChannel =
  | { readonly kind: "inPlace"; readonly manifestUrl: string }
  | { readonly kind: "link"; readonly manifestUrl: string }
  | { readonly kind: "mac"; readonly manifestUrl: string }
  | { readonly kind: "source" }
  | { readonly kind: "none" };

const NO_CHANNEL: UpdateChannel = { kind: "none" };

/** Read the `get_update_channel` payload. Anything unexpected means none. */
export function decodeUpdateChannel(raw: unknown): UpdateChannel {
  if (raw === null || typeof raw !== "object") return NO_CHANNEL;
  const obj = raw as Record<string, unknown>;
  const url = typeof obj.manifestUrl === "string" ? obj.manifestUrl : "";
  switch (obj.kind) {
    case "inPlace":
      return url.length > 0
        ? { kind: "inPlace", manifestUrl: url }
        : NO_CHANNEL;
    case "link":
      return url.length > 0 ? { kind: "link", manifestUrl: url } : NO_CHANNEL;
    case "mac":
      return url.length > 0 ? { kind: "mac", manifestUrl: url } : NO_CHANNEL;
    case "source":
      return { kind: "source" };
    default:
      return NO_CHANNEL;
  }
}

export async function fetchUpdateChannel(): Promise<UpdateChannel> {
  try {
    return decodeUpdateChannel(
      await tauriOnlyInvoke<unknown>("get_update_channel"),
    );
  } catch (error) {
    console.warn("Update channel unavailable", error);
    return NO_CHANNEL;
  }
}

/** A newer build: its version, and the download link for link installs. */
export type UpdateOffer = {
  readonly version: string;
  readonly link: Maybe<string>;
};

export type CheckFailure = "rateLimited" | "failed";

export type ManifestCheck =
  | { readonly kind: "upToDate" }
  | { readonly kind: "available"; readonly offer: UpdateOffer }
  | {
      readonly kind: "failed";
      readonly reason: CheckFailure;
      readonly message: string;
    };

const downloadWireSchema = z.object({
  version: z.string().min(1),
  url: z.string().min(1),
});

/** Packaged macOS: a newer version, with no file to download. */
const manualWireSchema = z.object({
  version: z.string().min(1),
  install: z.literal("manual"),
});

function bareVersion(raw: string): string {
  return raw.trim().replace(/^v/i, "");
}

function sameOrigin(a: string, b: string): boolean {
  try {
    return new URL(a).origin === new URL(b).origin;
  } catch {
    return false;
  }
}

function offerForVersion(
  version: string,
  current: string,
  link: Maybe<string>,
): ManifestCheck {
  // The server only answers 200 for a newer build (it compares semver,
  // prereleases included). Same version here means nothing to offer.
  if (bareVersion(version) === bareVersion(current)) {
    return { kind: "upToDate" };
  }
  return { kind: "available", offer: { version, link } };
}

/**
 * Turn a manifest response into a check result. A download link must be
 * on the cloud origin the manifest came from, since About opens it. A
 * manual manifest has no link.
 */
export function classifyManifestResponse(
  status: number,
  body: unknown,
  manifestUrl: string,
  current: string,
): ManifestCheck {
  if (status === 204) return { kind: "upToDate" };
  if (status === 429) {
    return {
      kind: "failed",
      reason: "rateLimited",
      message: "The update server is rate limiting checks (HTTP 429).",
    };
  }
  if (status !== 200) {
    return {
      kind: "failed",
      reason: "failed",
      message: `The update server answered HTTP ${String(status)}.`,
    };
  }
  const manual = decodeWithSchema(manualWireSchema, body, "Update manifest");
  if (manual.ok === true) {
    return offerForVersion(manual.value.version, current, nothing);
  }
  const decoded = decodeWithSchema(downloadWireSchema, body, "Update manifest");
  if (decoded.ok === false) {
    return { kind: "failed", reason: "failed", message: decoded.error };
  }
  if (sameOrigin(decoded.value.url, manifestUrl) === false) {
    return {
      kind: "failed",
      reason: "failed",
      message: "The update link is not on the Zephyrwrite cloud.",
    };
  }
  return offerForVersion(
    decoded.value.version,
    current,
    just(decoded.value.url),
  );
}

const CHECK_TIMEOUT_MS = 15_000;

/** GET the manifest. Network errors and timeouts come back as failed. */
export async function checkManifest(
  manifestUrl: string,
  current: string,
): Promise<ManifestCheck> {
  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort();
  }, CHECK_TIMEOUT_MS);
  try {
    const res = await fetch(manifestUrl, {
      headers: { accept: "application/json" },
      signal: controller.signal,
    });
    const body: unknown =
      res.status === 200 ? await res.json().catch(() => null) : null;
    return classifyManifestResponse(res.status, body, manifestUrl, current);
  } catch (error) {
    return {
      kind: "failed",
      reason: "failed",
      message: error instanceof Error ? error.message : String(error),
    };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Download, verify, and install the AppImage update. Ok(Just(version)) when
 * installed, Ok(Nothing) when the server has nothing newer anymore.
 */
export async function installAppUpdate(): Promise<
  Result<Maybe<string>, string>
> {
  try {
    const raw = await tauriOnlyInvoke<unknown>("install_app_update");
    if (raw === null) return ok(nothing);
    if (typeof raw === "string") return ok(just(raw));
    return err("Update install returned an unexpected result.");
  } catch (error) {
    return err(error instanceof Error ? error.message : String(error));
  }
}

export async function restartAfterUpdate(): Promise<void> {
  await tauriOnlyInvoke("restart_after_update");
}

/** Open a link download in the system browser. */
export async function openUpdateDownload(
  url: string,
): Promise<Result<null, string>> {
  try {
    await openUrl(url);
    return ok(null);
  } catch (error) {
    return err(error instanceof Error ? error.message : String(error));
  }
}
